import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Loader2, CheckCircle2, Users, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Campaign approve dialog (Phase 25).
 *
 * For each platform that has draft posts in the campaign, lets the user pick
 * one or more destinations (FB pages, IG accounts, LinkedIn personal/company).
 * Hitting "Approve all" calls /campaigns/{id}/approve-all with `targets_by_platform`.
 * Backend then flips drafts to scheduled and clones each draft per additional target,
 * preserving the AI-suggested time on every clone.
 */
export const CampaignApproveDialog = ({
  open,
  onOpenChange,
  campaignId,
  draftCount,
  draftPlatforms = [], // array of platform strings present in the campaign drafts
  onDone,
}) => {
  const { api } = useAuth();
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // selected[platform] = array of target keys ("platform::account_id")
  const [selected, setSelected] = useState({});
  // Auto-gen toggle (default ON). Defaults to nano-banana (Gemini) because it's
  // included in the Emergent Universal Key on every account and excels at photoreal.
  const [autoGenImages, setAutoGenImages] = useState(true);

  const keyOf = (t) => `${t.platform}::${t.account_id}`;

  // Group available targets by platform key (e.g. "facebook", "linkedin")
  const targetsByPlatform = useMemo(() => {
    const map = {};
    for (const t of targets) (map[t.platform] = map[t.platform] || []).push(t);
    return map;
  }, [targets]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/connections/targets').then((res) => {
      const list = res.data?.targets || [];
      setTargets(list);
      // Auto-select the first matching target for each draft platform so the
      // dialog is functional even if the user just clicks "Approve" without picking.
      const auto = {};
      for (const platform of draftPlatforms) {
        const norm = String(platform).toLowerCase().replace(/\s+/g, '_');
        const first = list.find((t) => t.platform === norm);
        if (first) auto[norm] = [keyOf(first)];
      }
      setSelected(auto);
    }).catch(() => {
      // silent — dialog still works, user can choose nothing and fall back to legacy
    }).finally(() => setLoading(false));
  }, [open, api, draftPlatforms]);

  const toggle = (platform, key) => {
    setSelected((cur) => {
      const arr = cur[platform] || [];
      const next = arr.includes(key) ? arr.filter((x) => x !== key) : [...arr, key];
      return { ...cur, [platform]: next };
    });
  };

  // Total scheduled posts after this approve action
  const totalEstimate = useMemo(() => {
    let total = 0;
    // Bucket drafts by normalized platform first
    const platformCounts = {};
    for (const p of draftPlatforms) {
      const norm = String(p).toLowerCase().replace(/\s+/g, '_');
      platformCounts[norm] = (platformCounts[norm] || 0) + 1;
    }
    for (const [platform, count] of Object.entries(platformCounts)) {
      const targetCount = (selected[platform] || []).length || 1; // at least 1 (legacy flip)
      total += count * targetCount;
    }
    return total;
  }, [selected, draftPlatforms]);

  const handleApprove = async () => {
    setBusy(true);
    try {
      // Convert selected keys → full target dicts grouped by platform
      const targets_by_platform = {};
      for (const [platform, keys] of Object.entries(selected)) {
        if (!keys || keys.length === 0) continue;
        targets_by_platform[platform] = keys.map((k) =>
          targets.find((t) => keyOf(t) === k)
        ).filter(Boolean);
      }
      const r = await api.post(`/campaigns/${campaignId}/approve-all`, {
        targets_by_platform,
        generate_images: autoGenImages,
        image_model: 'nano-banana',
      });
      const baseSummary = r.data.cloned > 0
        ? `${r.data.total_scheduled} posts scheduled (${r.data.updated} originals + ${r.data.cloned} clones)`
        : `${r.data.updated} posts scheduled`;
      const imgNote = r.data.image_generation === 'queued'
        ? ' — generating images in the background'
        : '';
      toast.success(baseSummary + imgNote);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally {
      setBusy(false);
    }
  };

  // Distinct platforms present among the drafts (normalized) — these are the
  // sections we render. If a platform has no available targets, the user can
  // still approve and the backend will fall back to legacy single-target flip.
  const draftPlatformsNormalized = useMemo(
    () => Array.from(new Set(draftPlatforms.map((p) => String(p).toLowerCase().replace(/\s+/g, '_')))),
    [draftPlatforms],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="campaign-approve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            Approve campaign — pick destinations
          </DialogTitle>
          <DialogDescription>
            {draftCount} draft post{draftCount === 1 ? '' : 's'} ready. Pick one or more destinations
            per platform. Each draft will be cloned for each destination.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {draftPlatformsNormalized.map((platform) => {
              const items = targetsByPlatform[platform] || [];
              const sel = selected[platform] || [];
              return (
                <div key={platform} className="space-y-1.5" data-testid={`approve-platform-${platform}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-zinc-700 capitalize flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-indigo-600" />
                      {platform.replace('_', ' ')}
                    </p>
                    {sel.length > 0 && (
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                        {sel.length} selected
                      </Badge>
                    )}
                  </div>
                  {items.length === 0 ? (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      No connected destinations for this platform — drafts will be scheduled with their existing platform setting.
                    </p>
                  ) : (
                    <div className="rounded-md border border-zinc-200 divide-y divide-zinc-100">
                      {items.map((t) => {
                        const k = keyOf(t);
                        const checked = sel.includes(k);
                        return (
                          <label
                            key={k}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-zinc-50 cursor-pointer"
                            data-testid={`approve-target-${platform}-${t.account_id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggle(platform, k)}
                              className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                            />
                            <span className="flex-1 truncate text-zinc-700">{t.account_name}</span>
                            {t.kind === 'org' && (
                              <span className="text-[9px] text-sky-700 bg-sky-50 px-1 rounded">Company</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
          Will create <strong>{totalEstimate}</strong> scheduled post{totalEstimate === 1 ? '' : 's'} total
          (each at its AI-suggested time).
        </div>

        {/* Auto-gen images toggle — saves users from manually hitting "Generate image"
            on every post card. Runs as a background task so this dialog returns instantly. */}
        <label className="flex items-start gap-2.5 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2.5 cursor-pointer hover:bg-indigo-50 transition-colors">
          <Checkbox
            checked={autoGenImages}
            onCheckedChange={(v) => setAutoGenImages(!!v)}
            className="mt-0.5 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
            data-testid="approve-autogen-toggle"
          />
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
              <span className="text-xs font-semibold text-indigo-900">
                Auto-generate images for all posts
              </span>
            </div>
            <p className="text-[11px] text-indigo-700/80 mt-0.5 leading-snug">
              Uses Nano Banana (Gemini · included on every account). Each image counts toward your monthly AI-image quota.
              Generation runs in the background — you can close this dialog and images populate as they're ready.
            </p>
          </div>
        </label>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            disabled={busy || loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="approve-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Approve & schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignApproveDialog;
